import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Loading, Page, Section } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { when } from "../lib/format.ts";

const OUTCOMES = ["created", "merged", "suggested", "skipped", "failed"] as const;
const SOURCE: Record<string, string> = { ghl: "GoHighLevel", rex: "Rex", ops: "Grassick Ops", vendor_portal: "Vendor portal", website: "Website" };

export function Imports() {
  const q = useQuery({ queryKey: ["imports"], queryFn: () => api.get<any[]>("/imports") });
  return (
    <Page title="Import review">
      <div className="banner"><Icon name="import" size={18} /><span>Imports run as server commands (<b>crm import ghl --dry-run</b>), on staging first. This copy imported a <b>made-up</b> GHL export to exercise the duplicate rules.</span></div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : (
        <Section title="Runs" count={q.data!.length}>
          {q.data!.length === 0 ? <Empty>No imports yet.</Empty> : (
            <div className="list">{q.data!.map((r) => (
              <Link key={r.id} to={`/imports/${r.id}`} className="row">
                <div className="main-col">
                  <div className="title">{SOURCE[r.source] ?? r.source}{r.dry_run && <span className="badge" style={{ marginLeft: 8 }}>Dry run</span>}</div>
                  <div className="sub">{OUTCOMES.map((o) => `${r.counts[o]} ${o}`).join(" · ")}</div>
                </div>
                <span className="when">{when(r.occurred_at)}</span>
              </Link>
            ))}</div>
          )}
        </Section>
      )}
    </Page>
  );
}

export function ImportRun() {
  const { id } = useParams();
  const [filter, setFilter] = useState<string>("");
  const q = useQuery({ queryKey: ["import", id], queryFn: () => api.get(`/imports/${id}`) });
  if (q.isLoading) return <Page title="Import" back="/imports"><Loading /></Page>;
  if (q.error || !q.data) return <Page title="Import" back="/imports"><ErrorView error={q.error} /></Page>;
  const r = q.data;
  const items = r.items.filter((i: any) => !filter || i.outcome === filter);
  return (
    <Page title={`${SOURCE[r.source] ?? r.source} import`} back="/imports">
      <div className="stats" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
        {OUTCOMES.map((o) => <button key={o} className={"stat" + (filter === o ? " hot" : "")} style={{ background: "none", border: 0, borderLeft: "1px solid var(--line)", textAlign: "left", color: "inherit" }}
          onClick={() => setFilter(filter === o ? "" : o)}><b className={"num" + (o === "failed" && r.counts[o] ? " danger" : "")}>{r.counts[o]}</b><span>{o}</span></button>)}
      </div>
      <div className="pad muted" style={{ fontSize: 13, marginTop: 10 }}>{r.file} · {r.dry_run ? "dry run, nothing kept" : "applied"} · {when(r.started_at)}</div>
      <Section title={filter ? filter : "Every record"} count={items.length}>
        <div className="scroll-x pad">
          <table className="table">
            <thead><tr><th>Record</th><th>Outcome</th><th>Why</th></tr></thead>
            <tbody>{items.map((i: any) => (
              <tr key={i.external_id}>
                <td>{i.person_id ? <Link className="blue" to={`/people/${i.person_id}`}>{i.name}</Link> : i.name}<div className="dim" style={{ fontSize: 12 }}>{i.external_id}</div></td>
                <td><span className={"badge" + (i.outcome === "failed" ? " danger" : i.outcome === "suggested" ? " blue" : i.outcome === "merged" ? " white" : "")}>{i.outcome}</span></td>
                <td className="muted">{i.rule.replace(/_/g, " ")}{i.note ? `: ${i.note}` : ""}{i.matched_person_id && <> · <Link className="blue" to={`/people/${i.matched_person_id}`}>match</Link></>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Section>
    </Page>
  );
}
